import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { justLetters, justNumbers } from '../../../../services/helpers';
import { MODE } from '../../../../interaces/mode.interface';
import {
  TOAST_ERROR_IDS,
  TOAST_INFO_IDS,
  TOAST_SUCCESS_IDS,
} from '../../../../interaces/toast.ids.interface';
import { certificateTypeSchema } from '../../../../schemas/certificateType.schema';
import { certificateTypesApi } from '../../api/certificateTypes.api';
import ConfirmationMessage from '../../../../components/GenericComponents/ConfirmationMessage';
import BoxConfirmation from '../../../../components/GenericComponents/BoxConfirmation';
import { streamData } from '../../../../services/helpers';
import { VALUE_DAY } from '../../interfaces/certificateTypes';
import { isNumber } from '../../../../services/helpers';
import { ESTATUS_BOOLEAN } from '../../../../catalogs/status';
import Select from 'react-select';
import { updateDataWhenCreate, updateDataWhenUpdate } from '../../../../services/helpers';
import styles from '../../styles/styles.module.css';

function CertificateTypeModal({
  onClose,
  model = null,
  mode = MODE.CREATE,
  setEntities = () => {},
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
    control,
  } = useForm({
    resolver: zodResolver(certificateTypeSchema),
    defaultValues: {
      name: '',
      description: '',
      active: false,
      indefiniteValidity: true,
      years: 0,
      months: 0,
      days: 0, // Used for validity calculations.
      xsd: false,
    },
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === MODE.UPDATE && model) {
      reset({
        name: model.name,
        description: model.description || '',
        active: model.active,
        indefiniteValidity: model.indefiniteValidity,
        years: model.years,
        months: model.months,
        days: model.days,
        xsd: model.xsd,
      });
      return;
    }
  }, [mode, model, reset]);

  const onSubmit = async (data) => {
    if (data.indefiniteValidity == true) {
      data.years = 0;
      data.months = 0;
      data.days = 0;
    } else {
      if (!isNumber(data.years) && !isNumber(data.months) && !isNumber(data.days)) {
        toast(TOAST_INFO_IDS.THE_DATE_MUST_HAVE_AT_LEAST_ONE_VALIDITY_VALUE, {
          icon: 'ℹ️',
          id: TOAST_INFO_IDS.THE_DATE_MUST_HAVE_AT_LEAST_ONE_VALIDITY_VALUE,
        });
      }
    }
    setData(data);
    setLoading(true);
    try {
      if (mode === MODE.CREATE) {
        const response = await certificateTypesApi.create(data);
        toast.success(TOAST_SUCCESS_IDS.RECORD_SAVED_SUCCESSFULLY, {
          id: TOAST_SUCCESS_IDS.RECORD_SAVED_SUCCESSFULLY,
        });
        updateDataWhenCreate(setEntities, response);
      } else {
        const sameData =
          model?.name === data.name &&
          (model?.description || '') === data.description &&
          Boolean(model?.active) === Boolean(data.active) && model.indefiniteValidity== data.indefiniteValidity;
        if (sameData) {
          toast(TOAST_INFO_IDS.NO_DATA_TO_UPDATE, {
            icon: 'ℹ️',
            id: TOAST_INFO_IDS.NO_DATA_TO_UPDATE,
          });
          setLoading(false);
          return;
        }
        // Ask for confirmation before disabling a certificate type.
        if (model.active == true && data.active == false) {
          setOPenModalConfirmation(true);
          return;
        }

        const response = await certificateTypesApi.update(model.id, data);
        toast.success(TOAST_SUCCESS_IDS.RECORD_UPDATED_SUCCESSFULLY, {
          id: TOAST_SUCCESS_IDS.RECORD_UPDATED_SUCCESSFULLY,
        });
        updateDataWhenUpdate(setEntities, response);
      }
      onClose();
    } catch (error) {
      toast.error(
        (error?.message && error.message !== 'Error'
          ? error.message
          : TOAST_ERROR_IDS.ERROR_PERFORMING_THIS_ACTION) ||
          TOAST_ERROR_IDS.ERROR_PERFORMING_THIS_ACTION,
        { id: TOAST_ERROR_IDS.TWO }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (watch('indefiniteValidity') == true) {
      setValue('years', 0);
      setValue('months', 0);
      setValue('days', 0);
    }
  }, [watch('indefiniteValidity')]);

  useEffect(() => {
    setValue('years', Number(watch('years')));
    setValue('months', Number(watch('months')));
    setValue('days', Number(watch('days')));
  }, [watch('years'), watch('months'), watch('days')]);

  useEffect(() => {
    if (!isNumber(watch('years'))) setValue('years', 0, { shouldValidate: true });
    if (!isNumber(watch('months'))) setValue('months', 0, { shouldValidate: true });
    if (!isNumber(watch('days'))) setValue('days', 0, { shouldValidate: true });
  }, [watch('years'), watch('months'), watch('days')]);

  const [openModalConfirmation, setOPenModalConfirmation] = useState(false);
  const [confirmDisableTypeCertificate, setConfirmDisableTypeCertificate] = useState(false);
  const [typeCertificateName, setTypeCertificateName] = useState('');
  const [data, setData] = useState(null);

  const onConfirmWithInput = async () => {
    setLoading(true);
    const newTypeCertificateNameStream = streamData(typeCertificateName);
    if (newTypeCertificateNameStream.length === 0) {
      setLoading(false);
      return toast(TOAST_INFO_IDS.ENTER_THE_REQUESTED_INFORNATION, {
        icon: 'ℹ️',
        id: TOAST_INFO_IDS.ENTER_THE_REQUESTED_INFORNATION,
      });
    }

    if (model.name !== newTypeCertificateNameStream) {
      setLoading(false);
      toast.error(TOAST_ERROR_IDS.NO_MATCH_DEPENDENCY_NAME, {
        id: TOAST_ERROR_IDS.NO_MATCH_DEPENDENCY_NAME,
      });
      return;
    }
    try {
      data.confirmationName = newTypeCertificateNameStream;
      streamData(data);
      const response = await certificateTypesApi.update(model.id, data);
      updateDataWhenUpdate(setEntities, response);
      onClose();
      toast.success(TOAST_SUCCESS_IDS.DEPENDENCY_SUCCESSFULLY_DISABLED, {
        id: TOAST_SUCCESS_IDS.DEPENDENCY_SUCCESSFULLY_DISABLED,
      });
    } catch (error) {
      toast.error(error.message || TOAST_ERROR_IDS.SERVER_CONNECTION_ERROR, {
        id: TOAST_ERROR_IDS.SERVER_CONNECTION_ERROR,
      });
    } finally {
      setLoading(false);
    }
  };
  const daysValidity = () => {
    return (
      (watch('years') || 0) * VALUE_DAY.YEAR +
      (watch('months') || 0) * VALUE_DAY.MONTH +
      (watch('days') || 0)
    );
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content modal-sizes">
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === MODE.UPDATE ? 'Editar tipo de certificado' : 'Nuevo tipo de certificado'}
          </h2>
          <button type="button" onClick={onClose} className="modal-close-btn">
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit(onSubmit)} className="modal-form">
            <div className="modal-div-columns">
              <div className="daysDiv">
                <label className="label-base">
                  Nombre<p className="modal-input-required">*</p>
                </label>
                <input
                  type="text"
                  className="input-base"
                  placeholder="Ej. Ciudadano"
                  {...register('name', { onChange: (e) => justLetters(e) })}
                />
                {errors.name && <span className="error-text">{errors.name.message}</span>}
              </div>

              {watch('xsd') && (
                <div className="daysDiv">
                  <label className="label-base">
                    Estado<span className="modal-input-required">*</span>
                  </label>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={ESTATUS_BOOLEAN}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id.toString()}
                        value={
                          ESTATUS_BOOLEAN.find((option) => option.value === field.value) || null
                        }
                        onChange={(option) => field.onChange(option?.value)}
                        placeholder="Todos los Estatus"
                        isSearchable={false}
                      />
                    )}
                  />
                  {errors.active && <span className="error-text">{errors.active.message}</span>}
                </div>
              )}
            </div>

            <div>
              <div className={styles.divVigence}>
                <div>
                  <label className="font-strong-bold ">
                    Vigencia
                    <span className="modal-input-required">*</span>
                  </label>
                </div>
                <div>
                  <label >
                    <input
                      type="checkbox"
                      {...register('indefiniteValidity')}
                    />
                    <span >Indefinida</span>
                  </label>

                  {errors.indefiniteValidity && (
                    <span className="error-text">{errors.indefiniteValidity.message}</span>
                  )}
                </div>
              </div>
            </div>

            {!watch('indefiniteValidity') && (
              <>
                <div className="daysDiv">
                  <div className="div-flex">
                    <div className={styles.daysDiv}>
                      <label className="label-base">
                        Años (1={VALUE_DAY.YEAR} días)
                        <span className="modal-input-required">*</span>
                      </label>
                      <Controller
                        name="years"
                        control={control}
                        render={({ field }) => (
                          <input
                            maxLength={3}
                            type="text"
                            className="input-atribute-form"
                            placeholder="Ej. 1"
                            value={field.value > 0 ? field.value : ''}
                            onChange={(e) => {
                              Number(e.target.value);
                              justNumbers(e);
                              field.onChange(e);
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        )}
                      />
                      {errors.years && <span className="error-text">{errors.years.message}</span>}
                    </div>
                    <div className={styles.daysDiv}>
                      <label className="label-base">
                        Meses (1= {VALUE_DAY.MONTH} días)
                        <span className="modal-input-required">*</span>
                      </label>
                      <Controller
                        name="months"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            maxLength={3}
                            className="input-atribute-form"
                            placeholder="Ej. 1"
                            value={field.value > 0 ? field.value : ''}
                            onChange={(e) => {
                              justNumbers(e);
                              field.onChange(e);
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        )}
                      />
                      {errors.months && <span className="error-text">{errors.months.message}</span>}
                    </div>
                    <div className={styles.daysDiv}>
                      <label className="label-base">
                        Días<span className="modal-input-required">*</span>
                      </label>
                      <Controller
                        name="days"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className="input-atribute-form"
                            maxLength={3}
                            placeholder="Ej. 1"
                            value={field.value > 0 ? field.value : ''}
                            onChange={(e) => {
                              justNumbers(e);
                              field.onChange(e);
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        )}
                      />
                      {errors.days && <span className="error-text">{errors.days.message}</span>}
                    </div>
                  </div>
                </div>

                <div className={styles.totalDays}>
                  <p>
                    {' '}
                    Lo anterior equivale a <strong className={styles.strongTotalDays}>
                      {daysValidity()}
                    </strong>{' '}
                    días de vigencia
                  </p>
                </div>
              </>
            )}

            <div className='modal-div-column-one'>
              <label className="label-base">Descripción</label>
              <textarea
                className={styles.textArea}
                rows="4"
                placeholder="Describe este tipo de certificado..."
                {...register('description')}
              />
              {errors.description && (
                <span className="error-text">{errors.description.message}</span>
              )}
            </div>

          <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-cancel">
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || (!isDirty && mode === MODE.UPDATE)}
              >
                {loading ? 'Guardando...' : mode === MODE.CREATE ? 'Guardar' : 'Actualizar'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {openModalConfirmation && (
        <ConfirmationMessage
          message={'¿Estas seguro de continuar con la desactivación de este tipo de certificado?'}
          onConfirm={() => setConfirmDisableTypeCertificate(true)}
          onCancel={() => {
            setOPenModalConfirmation(false);
            setLoading(false);
          }}
        />
      )}
      {confirmDisableTypeCertificate && (
        <BoxConfirmation
          maxLength={model.name.length}
          onConfirm={onConfirmWithInput}
          onCancel={() => {
            setConfirmDisableTypeCertificate(false);
            setLoading(false);
            setTypeCertificateName('');
          }}
          dependency={model}
          label={'Nombre del tipo de certificado:'}
          inputValue={typeCertificateName}
          setInputValue={setTypeCertificateName}
          placeHolder="Escribe el nombre del tipo de certificado"
          inUppercase={false}
          message={
            'Para confirmar esta acción, por favor ingresa el nombre del tipo de certificado'
          }
          name={model.name}
        />
      )}
    </div>
  );
}

export default CertificateTypeModal;
